
/* announcement-bar */
class AnnouncementBarCarousel extends HTMLElement {
  constructor() {
    super();
    this.slides = [];
    this.currentIndex = 0;
    this.interval = null;
    this.cycleSpeed = 5000;
    this.autoplayEnabled = false;
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll("p"));
    this.currentIndex = 0;

    const autoplayAttr = this.getAttribute("autoplay");
    this.autoplayEnabled = autoplayAttr !== null;
    if (this.autoplayEnabled && !isNaN(parseInt(autoplayAttr))) {
      this.cycleSpeed = parseInt(autoplayAttr) * 1000;
    }

    if (this.slides.length <= 1) return;

    // Set container styles for proper positioning
    this.style.position = "relative";
    this.style.overflow = "hidden";
    this.style.minHeight = "1em";

    // Style all slides and set is-selected only for the first
    this.slides.forEach((slide, index) => {
      slide.style.transition = "opacity 0.4s ease, transform 0.4s ease";
      slide.style.position = "absolute";
      slide.style.top = "0";
      slide.style.left = "0";
      slide.style.right = "0";
      slide.style.opacity = index === 0 ? "1" : "0";
      slide.style.transform = index === 0 ? "translateY(0)" : "translateY(100%)";
      slide.style.pointerEvents = index === 0 ? "auto" : "none";
      if (index === 0) {
        slide.classList.add("is-selected");
      } else {
        slide.classList.remove("is-selected");
      }
    });

    // Wait a tick to ensure DOM is ready
    requestAnimationFrame(() => {
      const id = this.id;
      if (!id) return;

      const prevButton = document.querySelector(`carousel-prev-button[aria-controls="${id}"] button`);
      const nextButton = document.querySelector(`carousel-next-button[aria-controls="${id}"] button`);

      if (prevButton) {
        prevButton.addEventListener("click", () => this.goToSlide(this.currentIndex - 1));
      }

      if (nextButton) {
        nextButton.addEventListener("click", () => this.goToSlide(this.currentIndex + 1));
      }
    });

    if (this.autoplayEnabled) {
      this.startAutoplay();
    }
  }

  startAutoplay() {
    this.interval = setInterval(() => this.goToSlide(this.currentIndex + 1), this.cycleSpeed);
  }

  stopAutoplay() {
    clearInterval(this.interval);
    this.interval = null;
  }

  goToSlide(newIndex) {
    if (this.slides.length < 2) return;

    this.stopAutoplay();

    const prev = this.slides[this.currentIndex];
    this.currentIndex = (newIndex + this.slides.length) % this.slides.length;
    const next = this.slides[this.currentIndex];

    // First, position the next slide below the viewport without transition
    next.style.transition = "none";
    next.style.opacity = "1";
    next.style.transform = "translateY(100%)";
    next.style.pointerEvents = "auto";
    next.classList.add("is-selected");

    // Force a reflow to ensure the initial position is applied
    next.offsetHeight;

    // Now start the transitions
    next.style.transition = "opacity 0.4s ease, transform 0.4s ease";
    
    // Move previous slide up and hide it
    prev.style.opacity = "0";
    prev.style.transform = "translateY(-100%)";
    prev.style.pointerEvents = "none";
    prev.classList.remove("is-selected");
    
    // Move next slide from below to center
    next.style.transform = "translateY(0)";

    if (this.autoplayEnabled) {
      this.startAutoplay();
    }
  }
}

if (!window.customElements.get("announcement-bar-carousel")) {
  window.customElements.define("announcement-bar-carousel", AnnouncementBarCarousel);
}

/* ============================================
   ARIA Live Region Announcements
   ============================================ */

/**
 * Announce a message to screen readers via ARIA live regions
 * @param {string} message - The message to announce
 * @param {string} priority - 'polite' for status updates, 'assertive' for alerts/errors
 */
function announceToScreenReader(message, priority = 'polite') {
  if (!message) return;
  
  const liveRegionId = priority === 'assertive' ? 'a11y-alert-message' : 'a11y-status-message';
  const liveRegion = document.getElementById(liveRegionId);
  
  if (!liveRegion) return;
  
  // Clear previous message
  liveRegion.textContent = '';
  
  // Use setTimeout to ensure the change is detected by screen readers
  setTimeout(() => {
    liveRegion.textContent = message;
    
    // Clear the message after it's been announced (5 seconds)
    setTimeout(() => {
      liveRegion.textContent = '';
    }, 5000);
  }, 100);
}

/**
 * Handle add to cart success
 */
document.addEventListener('product:added', function(event) {
  var detail = event.detail;
  var variantTitle = (detail.variant && detail.variant.title) ? detail.variant.title : '';
  var quantity = detail.quantity || 1;
  var productName = (detail.variant && detail.variant.product && detail.variant.product.title) ? detail.variant.product.title : 'Product';
  
  var message = quantity + ' ' + productName;
  if (variantTitle && variantTitle !== 'Default Title') {
    message += ' (' + variantTitle + ')';
  }
  message += quantity > 1 ? ' have been added to your cart' : ' has been added to your cart';
  
  if (window.announceToScreenReader) {
    announceToScreenReader(message, 'polite');
  }
});

/**
 * Handle cart update events
 */
document.addEventListener('cart:updated', function(event) {
  if (window.announceToScreenReader) {
    announceToScreenReader('Cart has been updated', 'polite');
  }
});

/**
 * Handle cart item removal
 */
document.addEventListener('cart:item-removed', function(event) {
  if (window.announceToScreenReader) {
    announceToScreenReader('Item removed from cart', 'polite');
  }
});

/**
 * Handle variant selection changes
 */
document.addEventListener('variant:changed', function(event) {
  if (event.detail && event.detail.variant) {
    var variant = event.detail.variant;
    var message = 'Selected: ';
    
    if (variant.title && variant.title !== 'Default Title') {
      message += variant.title;
    }
    
    if (variant.available) {
      message += ', Price: ' + variant.price;
    } else {
      message += ', Currently unavailable';
    }
    
    if (window.announceToScreenReader) {
      announceToScreenReader(message, 'polite');
    }
  }
});

/**
 * Handle filter/sort updates in collections
 */
document.addEventListener('facets:updated', function(event) {
  var resultsCount = event.detail && event.detail.count;
  if (resultsCount !== undefined && window.announceToScreenReader) {
    var message = 'Showing ' + resultsCount + ' ' + (resultsCount === 1 ? 'product' : 'products');
    announceToScreenReader(message, 'polite');
  } else if (window.announceToScreenReader) {
    announceToScreenReader('Products filtered', 'polite');
  }
});

/**
 * Handle search results updates
 */
document.addEventListener('search:updated', function(event) {
  var resultsCount = event.detail && event.detail.count;
  if (resultsCount !== undefined && window.announceToScreenReader) {
    var message = 'Found ' + resultsCount + ' ' + (resultsCount === 1 ? 'result' : 'results');
    announceToScreenReader(message, 'polite');
  }
});

/**
 * Handle form validation errors
 */
document.addEventListener('form:error', function(event) {
  var errorMessage = (event.detail && event.detail.message) ? event.detail.message : 'Please check the form for errors';
  if (window.announceToScreenReader) {
    announceToScreenReader(errorMessage, 'assertive');
  }
});

/**
 * Monitor for drawer open/close for screen reader announcements
 */
document.addEventListener('drawer:opened', function(event) {
  var drawerId = event.detail && event.detail.drawerId;
  var message = '';
  
  if (drawerId === 'sidebar-cart') {
    message = 'Shopping cart opened';
  } else if (drawerId === 'sidebar-menu') {
    message = 'Navigation menu opened';
  } else if (drawerId === 'collection-filter-drawer') {
    message = 'Filter menu opened';
  }
  
  if (message && window.announceToScreenReader) {
    announceToScreenReader(message, 'polite');
  }
});

/**
 * Announce when modals are opened
 */
if (window.Modal && window.Modal.prototype && window.Modal.prototype._openModal) {
  var originalModalOpen = window.Modal.prototype._openModal;
  window.Modal.prototype._openModal = function(modal) {
    originalModalOpen.call(this, modal);
    
    var modalTitleElement = modal.querySelector('.Modal__Header, [role="heading"]');
    if (modalTitleElement && modalTitleElement.textContent && window.announceToScreenReader) {
      var modalTitle = modalTitleElement.textContent.trim();
      if (modalTitle) {
        announceToScreenReader(modalTitle + ' dialog opened', 'polite');
      }
    }
  };
}

// Export for use in other scripts if needed
window.announceToScreenReader = announceToScreenReader;